import { VendorTypes, type VendorTypeEnum } from '../enums/VendorTypeEnum'
import { PaymentTerm, type PaymentTermConstructor } from '../PaymentTerm'
import {
  ShippingMethod,
  type ShippingMethodConstructor,
} from '../ShippingMethod'
import { ShippingTerm, type ShippingTermConstructor } from '../ShippingTerm'

export type EmailNotificationPreferences = {
  autoPo: boolean
  paymentRemittance: boolean
}

export type EmailConfiguration = Record<string, EmailNotificationPreferences>

export type VendorTaxDocumentType = 'w9' | 'w8ben' | 'w8bene'

export interface VendorTaxDocument {
  docType: VendorTaxDocumentType
  originalFilename: Nullable<string>
  uploadedAt: string
}

export interface VendorConstructor {
  id: number
  externalId: string
  name: string
  active: boolean
  address: {
    addressLineOne: string
    addressLineTwo: string
    postalCode: string
    city: string
    stateProvince: string
    country: string
  }
  contactInfo: {
    phoneOne: string
    phoneTwo: string
    fax: string
    email: string[]
    contact: string
    url: string
    emailConfigurations: Nullable<EmailConfiguration>
  }
  comments: string
  dateModified: ISODateString
  paymentTerm: Nullable<PaymentTermConstructor>
  shippingTerm: Nullable<ShippingTermConstructor>
  shippingMethod: Nullable<ShippingMethodConstructor>
  taxId: Nullable<number>
  legalName: Nullable<string>
  taxIdMasked: Nullable<string>
  taxIdTypeLabel: Nullable<string>
  taxCountry: Nullable<string>
  companyEmail: Nullable<string>
  companyPhone: Nullable<string>
  taxDocuments: VendorTaxDocument[]
  taxIdProvided: boolean
  taxDocumentUploaded: boolean
  type: VendorTypeEnum
  pdfLabelSet: string
  isAutoEmailPoEnabled: boolean
  request: Nullable<{
    id: number
    name: string
    requester: string
    submittedDate: ISODateString
    nextApproverName: Nullable<string>
    permissions: {
      canApprove: boolean
      canEdit: boolean
      canDelete: boolean
    }
  }>
}

export class Vendor {
  readonly id: number
  readonly externalId: string
  readonly name: string
  readonly active: boolean
  readonly address: {
    addressLineOne: string
    addressLineTwo: string
    postalCode: string
    city: string
    stateProvince: string
    country: string
  }
  contactInfo: {
    phoneOne: string
    phoneTwo: string
    fax: string
    email: string[]
    contact: string
    url: string
    emailConfigurations: Nullable<EmailConfiguration>
  }
  readonly comments: string
  readonly dateModified: ISODateString
  readonly paymentTerm: Nullable<PaymentTerm>
  readonly shippingTerm: Nullable<ShippingTerm>
  readonly shippingMethod: Nullable<ShippingMethod>
  readonly taxId: Nullable<number>
  // Registered legal name synced from the Vendor Portal (the 1099 name);
  // null = same as the vendor name.
  readonly legalName: Nullable<string>
  readonly taxIdMasked: Nullable<string>
  readonly taxIdTypeLabel: Nullable<string>
  readonly taxCountry: Nullable<string>
  // Vendor-portal-maintained company contact info, distinct from
  // contactInfo.phoneOne/phoneTwo (customer-owned).
  readonly companyEmail: Nullable<string>
  readonly companyPhone: Nullable<string>
  readonly taxDocuments: VendorTaxDocument[]
  readonly taxIdProvided: boolean
  readonly taxDocumentUploaded: boolean
  readonly type: VendorTypeEnum
  readonly pdfLabelSet: string
  readonly isAutoEmailPoEnabled: boolean
  readonly request: Nullable<{
    id: number
    name: string
    requester: string
    submittedDate: ISODateString
    nextApproverName: Nullable<string>
    permissions: {
      canApprove: boolean
      canEdit: boolean
      canDelete: boolean
    }
  }>

  constructor(data: VendorConstructor) {
    this.id = data.id
    this.externalId = data.externalId
    this.name = data.name
    this.active = data.active
    this.address = data.address
    this.contactInfo = data.contactInfo
    this.comments = data.comments
    this.dateModified = data.dateModified
    this.paymentTerm = data.paymentTerm
      ? new PaymentTerm(data.paymentTerm)
      : null
    this.shippingTerm = data.shippingTerm
      ? new ShippingTerm(data.shippingTerm)
      : null
    this.shippingMethod = data.shippingMethod
      ? new ShippingMethod(data.shippingMethod)
      : null
    this.taxId = data.taxId
    this.legalName = data.legalName
    this.taxIdMasked = data.taxIdMasked
    this.taxIdTypeLabel = data.taxIdTypeLabel
    this.taxCountry = data.taxCountry
    this.companyEmail = data.companyEmail
    this.companyPhone = data.companyPhone
    this.taxDocuments = data.taxDocuments
    this.taxIdProvided = data.taxIdProvided
    this.taxDocumentUploaded = data.taxDocumentUploaded
    this.type = data.type
    this.pdfLabelSet = data.pdfLabelSet
    this.isAutoEmailPoEnabled = data.isAutoEmailPoEnabled
    this.request = data.request
  }

  get isCreditCardProvider(): boolean {
    return this.type === VendorTypes.CREDIT_CARD_PROVIDER
  }

  get isPendingRequest(): boolean {
    return !!this.request && this.active === false
  }

  get isPreferred(): boolean {
    return this.type === VendorTypes.PREFERRED && !this.isPendingRequest
  }
}
